import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { Fragment } from 'react'

const getVisiblePages = (currentPage: number, totalPages: number) => {
  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1])
  return [...pages].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b)
}

interface ListPaginationProps {
  page: number
  pageSize: number
  total: number
  // Plural noun for the count, e.g. "fakturor"
  itemLabel: string
  onPageChange: (page: number) => void
}

export const ListPagination = ({ page, pageSize, total, itemLabel, onPageChange }: ListPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  const goTo = (target: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    onPageChange(target)
  }

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
      <p className="text-sm text-muted-foreground whitespace-nowrap">
        Visar {from.toLocaleString('sv-SE')}–{to.toLocaleString('sv-SE')} av {total.toLocaleString('sv-SE')}{' '}
        {itemLabel}
      </p>
      {totalPages > 1 && (
        <Pagination className="mx-0 w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                aria-disabled={page === 1}
                className={page === 1 ? 'pointer-events-none opacity-50' : undefined}
                onClick={goTo(Math.max(1, page - 1))}
              />
            </PaginationItem>
            {getVisiblePages(page, totalPages).map((visiblePage, index, pages) => (
              <Fragment key={visiblePage}>
                {index > 0 && visiblePage - pages[index - 1] > 1 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink href="#" isActive={visiblePage === page} onClick={goTo(visiblePage)}>
                    {visiblePage}
                  </PaginationLink>
                </PaginationItem>
              </Fragment>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                aria-disabled={page === totalPages}
                className={page === totalPages ? 'pointer-events-none opacity-50' : undefined}
                onClick={goTo(Math.min(totalPages, page + 1))}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  )
}
